import React, { ChangeEvent, Dispatch, SetStateAction, useEffect, useState } from 'react';
import { EntryField, EntryFieldProps } from '@noths/polaris-client-ribbons-design-system';
import { _Omit } from '@noths/polaris-dev-ts-recipes';

import { PersonalisationProductOption } from 'src/types/product';
import {
  MAX_CHARACTERS_COPY,
  NO_EMOJIS_ALLOWED_COPY,
  REQUIRED_INPUT_COPY,
} from '../../constants/copy';
import { containsEmoji, getTextCaseNotice } from '../../utils';

export interface InputOnChangeArgs {
  value: string;
  isValid: boolean;
}

export type OwnProps = _Omit<PersonalisationProductOption, 'type'> & {
  displayValidation?: boolean;
  allowLineBreaks?: boolean;
  required?: boolean;
  onChange: ({ value, isValid }: InputOnChangeArgs) => void;
};

interface ContainerProps {
  error: boolean;
  value?: string;
}

type Props = OwnProps & ContainerProps;

interface BlurHandlerProps extends Pick<OwnProps, 'onChange' | 'maxCharacters'> {
  e: ChangeEvent<HTMLTextAreaElement>;
  setErrorMessage: (value: string) => void;
}

interface ChangeHandlerProps extends Pick<OwnProps, 'allowLineBreaks'> {
  e: Parameters<EntryFieldProps['onChange']>[0];
  setStateValue: Dispatch<SetStateAction<string>>;
}

const isValidInputField = (value: string, maxCharacters: number) => {
  if (containsEmoji(value)) {
    return false;
  }

  return value.length <= maxCharacters;
};

/**
 * The majority of the input validation code happens here after blur is called
 * the onChange isValid boolean will handle preventing submit
 */
const handleBlur = ({ e, maxCharacters, onChange, setErrorMessage }: BlurHandlerProps) => {
  const { value } = e.target;
  const hasEmptyInputValue = value.length && value.trim().length === 0;
  const isValid = !hasEmptyInputValue && isValidInputField(value, maxCharacters);

  if (hasEmptyInputValue) {
    setErrorMessage(REQUIRED_INPUT_COPY);
  }

  onChange({ value, isValid });
};

const handleChange = ({ e, allowLineBreaks, setStateValue }: ChangeHandlerProps) => {
  const { value } = e.target;
  const isLineBreak = e.nativeEvent.inputType === 'insertLineBreak';

  if (!allowLineBreaks && isLineBreak) {
    return;
  }

  const hasDisallowedNewLines = !allowLineBreaks && (value.includes('\n') || value.includes('\r'));

  /*
    The Regex replacement needs to be conditionally run, instead of for every change.
    Asides from performance implications, all whitespaces get removed when no newlines are detected.
  */
  const sanitisedValue = hasDisallowedNewLines ? value.replace(/\s+/g, ' ').trim() : value;

  setStateValue(() => sanitisedValue);
};

export const Input = ({
  error,
  value = '',
  required = false,
  onChange,
  maxCharacters,
  allowLineBreaks = true,
  minCharacters,
  allowedCase,
  name,
  id,
}: Props) => {
  const [stateValue, setStateValue] = useState(value);
  const [errorMessage, setErrorMessage] = useState(error ? REQUIRED_INPUT_COPY : '');

  useEffect(() => {
    const hasExcessiveInputCharLength = stateValue.length > maxCharacters;

    if (hasExcessiveInputCharLength) {
      setErrorMessage(MAX_CHARACTERS_COPY);
    } else if (containsEmoji(stateValue)) {
      setErrorMessage(NO_EMOJIS_ALLOWED_COPY);
    } else if (error) {
      setErrorMessage(REQUIRED_INPUT_COPY);
    } else {
      setErrorMessage('');
    }
  }, [error, maxCharacters, stateValue]);

  // Only used if value prop gets updated to refresh input stateValue
  useEffect(() => {
    if (value !== stateValue) {
      setStateValue(value);
      onChange({ value, isValid: isValidInputField(value, maxCharacters) });
    }
  }, [value]);

  return (
    <EntryField
      value={stateValue}
      id={id.toString()}
      placeholder={`Max ${maxCharacters} characters`}
      error={errorMessage}
      description={allowedCase ? getTextCaseNotice(allowedCase) : ''}
      onBlur={(e) => handleBlur({ e, maxCharacters, onChange, setErrorMessage })}
      required={required}
      onChange={(e) => handleChange({ e, setStateValue, allowLineBreaks })}
      minLength={minCharacters}
      name={name}
      feedback={{
        name: errorMessage || '',
        value: maxCharacters - stateValue.length,
      }}
    />
  );
};
